import { create } from 'zustand';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiConfig } from '../amplify-config';
import type {
  ContentCard,
  CreateContentRequest,
  UpdateContentRequest,
  GenerateContentRequest,
  CardType,
  CardStatus,
} from '../types/content';

// Polling interval for translation status updates (5 seconds)
const TRANSLATION_POLL_INTERVAL_MS = 5000;
// Polling interval for generation status updates (5 seconds)
const GENERATION_POLL_INTERVAL_MS = 5000;

interface ContentFilters {
  cardType?: CardType;
  status?: CardStatus;
  publishDate?: string;
  /** "Showing now" view (VAP-823): the cards the app shows today. The server picks the day
   *  the same way content-fetch does, so the panel and the app agree around midnight.
   *  Overrides publishDate and status. */
  showingNow?: boolean;
}

interface ContentState {
  // Data
  cards: ContentCard[];
  selectedCards: Set<string>;
  currentCard: ContentCard | null;
  // Opaque token for the next page of the current list; null when the list is complete
  nextToken: string | null;
  
  // UI State
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  filters: ContentFilters;
  
  // Translation polling
  pollingCardIds: Set<string>;
  pollingIntervalId: number | null;
  
  // Generation polling
  generationPollingCardIds: Set<string>;
  generationPollingIntervalId: number | null;
  
  // Actions
  fetchCards: (filters?: ContentFilters) => Promise<void>;
  loadMoreCards: () => Promise<void>;
  fetchCard: (cardId: string) => Promise<void>;
  createCard: (data: CreateContentRequest) => Promise<ContentCard | null>;
  updateCard: (cardId: string, data: UpdateContentRequest) => Promise<ContentCard | null>;
  deleteCard: (cardId: string) => Promise<boolean>;
  bulkDeleteCards: (cardIds: string[]) => Promise<boolean>;
  generateContent: (data: GenerateContentRequest) => Promise<ContentCard | null>;
  
  // Status actions (Requirements 3.2, 4.2, 6.6)
  updateCardStatus: (cardId: string, status: CardStatus) => Promise<ContentCard | null>;
  bulkUpdateStatus: (cardIds: string[], status: CardStatus) => Promise<boolean>;
  
  // Translation actions
  retryTranslations: (cardId: string) => Promise<boolean>;
  startPollingTranslations: () => void;
  stopPollingTranslations: () => void;
  
  // Generation actions
  retryGeneration: (cardId: string) => Promise<boolean>;
  startPollingGeneration: () => void;
  stopPollingGeneration: () => void;
  
  // Selection
  toggleCardSelection: (cardId: string) => void;
  selectAllCards: () => void;
  clearSelection: () => void;
  
  // Error handling
  clearError: () => void;
}

async function getAuthHeaders(): Promise<HeadersInit> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  } catch {
    return {
      'Content-Type': 'application/json',
    };
  }
}

interface CardsPage {
  cards: ContentCard[];
  nextToken: string | null;
}

// One GET /content request. nextToken continues a previous page.
async function fetchCardsPage(filters: ContentFilters, nextToken: string | null): Promise<CardsPage> {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams();
  if (filters.cardType) params.append('cardType', filters.cardType);
  if (filters.showingNow) {
    params.append('view', 'showing');
  } else {
    if (filters.status) params.append('status', filters.status);
    if (filters.publishDate) params.append('publishDate', filters.publishDate);
  }
  if (nextToken) params.append('nextToken', nextToken);

  const url = `${apiConfig.adminApiUrl}/content${params.toString() ? `?${params}` : ''}`;
  const response = await fetch(url, { headers });
  if (!response.ok) {
    throw new Error(`Failed to fetch cards: ${response.statusText}`);
  }
  const data = await response.json();
  return {
    cards: (data.cards || []) as ContentCard[],
    nextToken: typeof data.nextToken === 'string' && data.nextToken ? data.nextToken : null,
  };
}

type StoreSet = (partial: Partial<ContentState> | ((state: ContentState) => Partial<ContentState>)) => void;

/** The cards the app shows today (VAP-823). Does not touch the store's list or filters. */
export async function fetchShowingNow(): Promise<ContentCard[]> {
  const page = await fetchCardsPage({ showingNow: true }, null);
  return page.cards;
}

// The bulk Lambdas accept at most 25 ids per call; "Select all" can select 100 loaded cards.
const BULK_BATCH_SIZE = 25;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// Read the Lambda's error text; it may be a string or an object
async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const data = await response.json();
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && typeof data.error.message === 'string') return data.error.message;
  } catch {
    // no JSON body
  }
  return fallback;
}
type StoreGet = () => ContentState;

// Start or stop the translation/generation pollers to match the cards on screen
function syncPolling(cards: ContentCard[], set: StoreSet, get: StoreGet): void {
  const translatingCardIds = new Set<string>(
    cards.filter(card => card.translationStatus === 'TRANSLATING').map(card => card.cardId)
  );
  const generatingCardIds = new Set<string>(
    cards.filter(card => card.generationStatus === 'GENERATING').map(card => card.cardId)
  );

  set({ pollingCardIds: translatingCardIds, generationPollingCardIds: generatingCardIds });

  if (translatingCardIds.size > 0) {
    get().startPollingTranslations();
  } else {
    get().stopPollingTranslations();
  }

  if (generatingCardIds.size > 0) {
    get().startPollingGeneration();
  } else {
    get().stopPollingGeneration();
  }
}

// Incremented on every fetchCard call; a response whose id is no longer current is dropped
let fetchCardSequence = 0;

// Start the translation/generation pollers for one card (Edit page after create, fetch or save).
// Adds to the sets instead of replacing them, so cards already watched by the list keep polling.
function watchCard(card: ContentCard, set: StoreSet, get: StoreGet): void {
  if (card.translationStatus === 'TRANSLATING') {
    set(state => ({ pollingCardIds: new Set([...state.pollingCardIds, card.cardId]) }));
    get().startPollingTranslations();
  }
  if (card.generationStatus === 'GENERATING') {
    set(state => ({ generationPollingCardIds: new Set([...state.generationPollingCardIds, card.cardId]) }));
    get().startPollingGeneration();
  }
}

export const useContentStore = create<ContentState>((set, get) => ({
  // Initial state
  cards: [],
  selectedCards: new Set(),
  currentCard: null,
  nextToken: null,
  loading: false,
  loadingMore: false,
  error: null,
  filters: {},
  pollingCardIds: new Set(),
  pollingIntervalId: null,
  generationPollingCardIds: new Set(),
  generationPollingIntervalId: null,

  // Fetch the first page of cards with optional filters
  fetchCards: async (filters?: ContentFilters) => {
    const activeFilters = filters || get().filters;
    set({ loading: true, error: null, filters: activeFilters });
    try {
      const page = await fetchCardsPage(activeFilters, null);
      // Drop the result if the filters changed while this request was in flight
      if (get().filters !== activeFilters) {
        return;
      }
      set({ cards: page.cards, nextToken: page.nextToken, loading: false, selectedCards: new Set() });
      syncPolling(page.cards, set, get);
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch cards',
        loading: false 
      });
    }
  },

  // Append the next page of cards for the current filters (VAP-823)
  loadMoreCards: async () => {
    const { nextToken, filters, loadingMore, loading } = get();
    if (!nextToken || loadingMore || loading) {
      return;
    }
    set({ loadingMore: true, error: null });
    try {
      const page = await fetchCardsPage(filters, nextToken);
      if (get().filters !== filters) {
        set({ loadingMore: false });
        return;
      }
      set(state => {
        const known = new Set(state.cards.map(c => c.cardId));
        const appended = page.cards.filter(c => !known.has(c.cardId));
        return { cards: [...state.cards, ...appended], nextToken: page.nextToken, loadingMore: false };
      });
      syncPolling(get().cards, set, get);
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load more cards',
        loadingMore: false 
      });
    }
  },

  // Fetch single card. A response for an older card id is dropped when a newer fetch started
  // in between (fast navigation from card A to card B), so B's page never shows A's data.
  fetchCard: async (cardId: string) => {
    const requestId = ++fetchCardSequence;
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}`, { headers });
      if (requestId !== fetchCardSequence) {
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Failed to fetch card: ${response.statusText}`);
      }
      
      const data = await response.json();
      if (requestId !== fetchCardSequence) {
        return;
      }
      set({ currentCard: data.card, loading: false });
      watchCard(data.card as ContentCard, set, get);
    } catch (error) {
      if (requestId !== fetchCardSequence) {
        return;
      }
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch card',
        loading: false 
      });
    }
  },

  // Create new card
  createCard: async (data: CreateContentRequest) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create card');
      }
      
      const result = await response.json();
      const newCard = result.card;
      
      // Add to local state
      set(state => ({
        cards: [newCard, ...state.cards],
        loading: false,
      }));
      watchCard(newCard, set, get);
      
      return newCard;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create card',
        loading: false 
      });
      return null;
    }
  },

  // Update existing card
  updateCard: async (cardId: string, data: UpdateContentRequest) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update card');
      }
      
      const result = await response.json();
      const updatedCard = result.card;
      
      // Update local state
      set(state => ({
        cards: state.cards.map(c => c.cardId === cardId ? updatedCard : c),
        currentCard: state.currentCard?.cardId === cardId ? updatedCard : state.currentCard,
        loading: false,
      }));
      watchCard(updatedCard, set, get);
      
      return updatedCard;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update card',
        loading: false 
      });
      return null;
    }
  },

  // Delete single card
  deleteCard: async (cardId: string) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}`, {
        method: 'DELETE',
        headers,
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete card');
      }
      
      // Remove from local state
      set(state => ({
        cards: state.cards.filter(c => c.cardId !== cardId),
        selectedCards: new Set([...state.selectedCards].filter(id => id !== cardId)),
        loading: false,
      }));
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete card',
        loading: false 
      });
      return false;
    }
  },

  // Bulk delete cards
  bulkDeleteCards: async (cardIds: string[]) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      for (const batch of chunk(cardIds, BULK_BATCH_SIZE)) {
        const response = await fetch(`${apiConfig.adminApiUrl}/content`, {
          method: 'DELETE',
          headers,
          body: JSON.stringify({ cardIds: batch }),
        });
        if (!response.ok) {
          throw new Error(await readErrorMessage(response, 'Failed to delete cards'));
        }
      }
      
      // Remove from local state
      const deletedSet = new Set(cardIds);
      set(state => ({
        cards: state.cards.filter(c => !deletedSet.has(c.cardId)),
        selectedCards: new Set(),
        loading: false,
      }));
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete cards',
        loading: false 
      });
      return false;
    }
  },

  // Generate AI content
  generateContent: async (data: GenerateContentRequest) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate content');
      }
      
      const result = await response.json();
      const newCard = result.card;
      
      // Add to local state
      set(state => ({
        cards: [newCard, ...state.cards],
        loading: false,
      }));
      
      // If the card is generating, start generation polling
      if (newCard.generationStatus === 'GENERATING') {
        set(state => ({
          generationPollingCardIds: new Set([...state.generationPollingCardIds, newCard.cardId]),
        }));
        get().startPollingGeneration();
      }
      
      // If the card is translating, start translation polling
      if (newCard.translationStatus === 'TRANSLATING') {
        set(state => ({
          pollingCardIds: new Set([...state.pollingCardIds, newCard.cardId]),
        }));
        get().startPollingTranslations();
      }
      
      return newCard;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to generate content',
        loading: false 
      });
      return null;
    }
  },

  // Update single card status (Requirements 3.2, 4.2)
  updateCardStatus: async (cardId: string, status: CardStatus) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}/status`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${status === 'PUBLISHED' ? 'publish' : 'unpublish'} card`);
      }
      
      const result = await response.json();
      const updatedCard = result.card;
      
      // Update local state
      set(state => ({
        cards: state.cards.map(c => c.cardId === cardId ? updatedCard : c),
        currentCard: state.currentCard?.cardId === cardId ? updatedCard : state.currentCard,
        loading: false,
      }));
      
      return updatedCard;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : `Failed to ${status === 'PUBLISHED' ? 'publish' : 'unpublish'} card`,
        loading: false 
      });
      return null;
    }
  },

  // Bulk update card statuses (Requirement 6.6)
  bulkUpdateStatus: async (cardIds: string[], status: CardStatus) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      for (const batch of chunk(cardIds, BULK_BATCH_SIZE)) {
        const response = await fetch(`${apiConfig.adminApiUrl}/content/bulk-status`, {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ cardIds: batch, status }),
        });
        if (!response.ok) {
          throw new Error(await readErrorMessage(response, `Failed to ${status === 'PUBLISHED' ? 'publish' : 'unpublish'} cards`));
        }
      }
      
      // Update local state - update status for all affected cards
      set(state => ({
        cards: state.cards.map(c => 
          cardIds.includes(c.cardId) ? { ...c, status } : c
        ),
        selectedCards: new Set(),
        loading: false,
      }));
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : `Failed to ${status === 'PUBLISHED' ? 'publish' : 'unpublish'} cards`,
        loading: false 
      });
      return false;
    }
  },

  // Retry failed translations for a card. Callers track their own busy flag, so this does not
  // touch the global `loading` (which would disable the unrelated Save button on the Edit page).
  retryTranslations: async (cardId: string) => {
    set({ error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}/retry`, {
        method: 'PATCH',
        headers,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to retry translations');
      }
      
      const result = await response.json();
      const updatedCard = result.card;
      
      // Update local state
      set(state => ({
        cards: state.cards.map(c => c.cardId === cardId ? updatedCard : c),
        currentCard: state.currentCard?.cardId === cardId ? updatedCard : state.currentCard,
      }));
      watchCard(updatedCard, set, get);
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to retry translations',
      });
      return false;
    }
  },

  // Start polling for translation status updates
  startPollingTranslations: () => {
    const { pollingIntervalId, pollingCardIds } = get();
    
    // Don't start if already polling or no cards to poll
    if (pollingIntervalId !== null || pollingCardIds.size === 0) {
      return;
    }
    
    const intervalId = window.setInterval(async () => {
      const { pollingCardIds: currentPollingIds } = get();
      
      if (currentPollingIds.size === 0) {
        get().stopPollingTranslations();
        return;
      }
      
      try {
        const headers = await getAuthHeaders();
        
        // Fetch updated status for each translating card
        const updatedCards = await Promise.all(
          Array.from(currentPollingIds).map(async (cardId) => {
            const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}`, { headers });
            if (response.ok) {
              const data = await response.json();
              return data.card as ContentCard;
            }
            return null;
          })
        );
        
        // Update cards in state and remove completed ones from polling
        const stillTranslating = new Set<string>();
        const cardUpdates = new Map<string, ContentCard>();
        
        for (const updatedCard of updatedCards) {
          if (updatedCard) {
            cardUpdates.set(updatedCard.cardId, updatedCard);
            if (updatedCard.translationStatus === 'TRANSLATING') {
              stillTranslating.add(updatedCard.cardId);
            }
          }
        }
        
        set(state => ({
          cards: state.cards.map(c => cardUpdates.get(c.cardId) || c),
          currentCard: state.currentCard && cardUpdates.has(state.currentCard.cardId) 
            ? cardUpdates.get(state.currentCard.cardId)! 
            : state.currentCard,
          pollingCardIds: stillTranslating,
        }));
        
        // Stop polling if no more translating cards
        if (stillTranslating.size === 0) {
          get().stopPollingTranslations();
        }
      } catch (error) {
        console.error('Error polling translation status:', error);
      }
    }, TRANSLATION_POLL_INTERVAL_MS);
    
    set({ pollingIntervalId: intervalId });
  },

  // Stop polling for translation status updates
  stopPollingTranslations: () => {
    const { pollingIntervalId } = get();
    if (pollingIntervalId !== null) {
      window.clearInterval(pollingIntervalId);
      set({ pollingIntervalId: null });
    }
  },

  // Retry failed generation for a card (Requirement 10.10)
  retryGeneration: async (cardId: string) => {
    set({ loading: true, error: null });
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}/retry-generation`, {
        method: 'PATCH',
        headers,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to retry generation');
      }
      
      const result = await response.json();
      const updatedCard = result.card;
      
      // Update local state
      set(state => ({
        cards: state.cards.map(c => c.cardId === cardId ? updatedCard : c),
        currentCard: state.currentCard?.cardId === cardId ? updatedCard : state.currentCard,
        loading: false,
        // Add to generation polling set since it's now generating
        generationPollingCardIds: new Set([...state.generationPollingCardIds, cardId]),
      }));
      
      // Start polling for generation updates
      get().startPollingGeneration();
      
      return true;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to retry generation',
        loading: false 
      });
      return false;
    }
  },

  // Start polling for generation status updates (Requirement 10.9)
  startPollingGeneration: () => {
    const { generationPollingIntervalId, generationPollingCardIds } = get();
    
    // Don't start if already polling or no cards to poll
    if (generationPollingIntervalId !== null || generationPollingCardIds.size === 0) {
      return;
    }
    
    const intervalId = window.setInterval(async () => {
      const { generationPollingCardIds: currentPollingIds } = get();
      
      if (currentPollingIds.size === 0) {
        get().stopPollingGeneration();
        return;
      }
      
      try {
        const headers = await getAuthHeaders();
        
        // Fetch updated status for each generating card
        const updatedCards = await Promise.all(
          Array.from(currentPollingIds).map(async (cardId) => {
            const response = await fetch(`${apiConfig.adminApiUrl}/content/${cardId}`, { headers });
            if (response.ok) {
              const data = await response.json();
              return data.card as ContentCard;
            }
            return null;
          })
        );
        
        // Update cards in state and remove completed ones from polling
        const stillGenerating = new Set<string>();
        const cardUpdates = new Map<string, ContentCard>();
        
        for (const updatedCard of updatedCards) {
          if (updatedCard) {
            cardUpdates.set(updatedCard.cardId, updatedCard);
            if (updatedCard.generationStatus === 'GENERATING') {
              stillGenerating.add(updatedCard.cardId);
            }
            // If generation completed and translation started, add to translation polling
            if (updatedCard.generationStatus === 'GENERATED' && updatedCard.translationStatus === 'TRANSLATING') {
              set(state => ({
                pollingCardIds: new Set([...state.pollingCardIds, updatedCard.cardId]),
              }));
              get().startPollingTranslations();
            }
          }
        }
        
        set(state => ({
          cards: state.cards.map(c => cardUpdates.get(c.cardId) || c),
          currentCard: state.currentCard && cardUpdates.has(state.currentCard.cardId) 
            ? cardUpdates.get(state.currentCard.cardId)! 
            : state.currentCard,
          generationPollingCardIds: stillGenerating,
        }));
        
        // Stop polling if no more generating cards
        if (stillGenerating.size === 0) {
          get().stopPollingGeneration();
        }
      } catch (error) {
        console.error('Error polling generation status:', error);
      }
    }, GENERATION_POLL_INTERVAL_MS);
    
    set({ generationPollingIntervalId: intervalId });
  },

  // Stop polling for generation status updates
  stopPollingGeneration: () => {
    const { generationPollingIntervalId } = get();
    if (generationPollingIntervalId !== null) {
      window.clearInterval(generationPollingIntervalId);
      set({ generationPollingIntervalId: null });
    }
  },

  // Selection actions
  toggleCardSelection: (cardId: string) => {
    set(state => {
      const newSelection = new Set(state.selectedCards);
      if (newSelection.has(cardId)) {
        newSelection.delete(cardId);
      } else {
        newSelection.add(cardId);
      }
      return { selectedCards: newSelection };
    });
  },

  selectAllCards: () => {
    set(state => ({
      selectedCards: new Set(state.cards.map(c => c.cardId)),
    }));
  },

  clearSelection: () => {
    set({ selectedCards: new Set() });
  },

  // Error handling
  clearError: () => {
    set({ error: null });
  },
}));
